import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PauseCircle, Search, Trash2 } from "lucide-react";
import { api, DOC_LABELS, money, shopDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPhone } from "@/lib/phone";

export default function ParkedInvoices() {
  const { branch } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [bothBranches, setBothBranches] = useState(false);

  const load = () => {
    api
      .get("/documents", {
        params: {
          status: "parked",
          branch: bothBranches ? undefined : branch,
          search: search.trim() || undefined,
          limit: 200,
        },
      })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, search, bothBranches]);

  const discard = async (row) => {
    if (!window.confirm(
      row.customer_name
        ? `Discard parked docket #${row.doc_number} for ${row.customer_name}? The number is kept and marked VOID, never deleted.`
        : `Give up number #${row.doc_number}? It is kept and marked "Blank number — never used", so the series has no gap.`
    ))
      return;
    try {
      await api.post(`/documents/${row.id}/discard`, {});
      toast.success(
        row.customer_name
          ? `#${row.doc_number} voided — the number stays in the series`
          : `#${row.doc_number} marked unused — the number stays in the series`
      );
      load();
    } catch (e) {
      toast.error("Could not discard that docket");
    }
  };

  const parkedAt = (v) => shopDateTime(v);

  return (
    <div className="fade-up" data-testid="parked-invoices-page">
      <h1 className="text-3xl font-bold sm:text-4xl">Parked / On Hold</h1>
      <p className="mt-2 text-sm text-muted-foreground md:text-base">
        Dockets put aside mid-sale. Nothing is finalised until you complete them.
      </p>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 rounded-none pl-9"
            placeholder="Search phone, name or docket number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="parked-search-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={bothBranches}
            onChange={(e) => setBothBranches(e.target.checked)}
            data-testid="parked-both-branches"
          />
          Both branches
        </label>
      </div>

      {rows.length === 0 ? (
        <div
          className="mt-10 border border-dashed border-border py-16 text-center"
          data-testid="parked-empty-state"
        >
          <PauseCircle className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            No parked dockets. Use “Park &amp; serve next” on an invoice to hold it here.
          </p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto border border-border">
          <table className="w-full min-w-[900px] text-sm" data-testid="parked-table">
            <thead className="bg-[#F8F9FA] text-left">
              <tr className="border-b border-border">
                <th className="px-3 py-2.5 font-semibold">Docket</th>
                <th className="px-3 py-2.5 font-semibold">Customer</th>
                <th className="px-3 py-2.5 font-semibold">Phone</th>
                <th className="px-3 py-2.5 font-semibold">Staff</th>
                <th className="px-3 py-2.5 font-semibold">Parked</th>
                <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                <th className="px-3 py-2.5 text-right font-semibold">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={r.id} className="hover:bg-[#FBF8F2]" data-testid={`parked-row-${i}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold">#{r.doc_number}</div>
                    <div className="text-xs text-muted-foreground">{DOC_LABELS[r.doc_type]}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    {r.customer_name || (
                      <span className="text-xs text-muted-foreground" data-testid={`parked-blank-${i}`}>
                        Number reserved — nothing entered yet
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">{r.customer_phone ? formatPhone(r.customer_phone) : "-"}</td>
                  <td className="px-3 py-2.5">{r.parked_by || r.created_by}</td>
                  <td className="px-3 py-2.5">{parkedAt(r.parked_at || r.created_at)}</td>
                  <td className="tabular px-3 py-2.5 text-right">{money(r.total)}</td>
                  <td className="tabular px-3 py-2.5 text-right">{money(r.total_paid)}</td>
                  <td className="tabular px-3 py-2.5 text-right font-semibold">
                    {money(r.balance)}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      onClick={() => navigate(`/documents/${r.id}/edit`)}
                      data-testid={`resume-parked-${i}`}
                    >
                      Resume
                    </Button>
                    <button
                      type="button"
                      onClick={() => discard(r)}
                      className="ml-2 p-1.5 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                      data-testid={`discard-parked-${i}`}
                      aria-label="Discard docket"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
